import { createFileRoute, notFound, redirect } from '@tanstack/react-router'
import Pagination from '@/components/editorial/Pagination'
import { PAGE_SIZE, pageUrl, validatePageSearch } from '@/lib/pagination'
import EditorialHero from '@/components/editorial/EditorialHero'
import EditorialPostList from '@/components/editorial/EditorialPostList'
import SectionHeader from '@/components/editorial/SectionHeader'
import Reveal from '@/components/preview/Reveal'
import { getCategoryPageData } from '@/lib/postServerFns'

export const Route = createFileRoute('/blog/$category/')({
  validateSearch: validatePageSearch,
  loaderDeps: ({ search }) => search,
  loader: async ({ params: { category }, deps }) => {
    if (deps.page === 0) throw notFound()
    if (deps.page === 1)
      throw redirect({
        to: '/blog/$category',
        params: { category },
        statusCode: 301,
      })
    const data = await getCategoryPageData({
      data: { category, page: deps.page },
    })
    if (!data.category || !data.validPage) throw notFound()
    return { ...data, category: data.category, markdownSearch: deps }
  },
  component: CategoryPage,
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: '404 — Category Not Found | Suriyaprakash Somu' },
          { name: 'robots', content: 'noindex, follow' },
          {
            name: 'description',
            content: 'The requested blog category does not exist.',
          },
          { property: 'og:title', content: '404 — Category Not Found' },
          {
            property: 'og:description',
            content: 'The requested blog category does not exist.',
          },
          { name: 'twitter:title', content: '404 — Category Not Found' },
          {
            name: 'twitter:description',
            content: 'The requested blog category does not exist.',
          },
        ],
      }
    }

    const categoryUrl = pageUrl(
      `https://www.suriyaprakash.in/blog/${loaderData.category.slug}`,
      loaderData.page,
    )
    const title = `${loaderData.category.name} Guides & Learning Paths${loaderData.page > 1 ? ` — Page ${loaderData.page}` : ''} | Suriya`
    const description = `${loaderData.category.description}${loaderData.page > 1 ? ` Page ${loaderData.page} of ${loaderData.totalPages}.` : ''}`
    const aiSummary = `This category contains ${loaderData.totalItems} ${loaderData.totalItems === 1 ? 'article' : 'articles'} by Suriyaprakash Somu about ${loaderData.category.name}. ${description}`

    return {
      meta: [
        {
          title,
        },
        {
          name: 'title',
          content: title,
        },
        {
          name: 'description',
          content: description,
        },
        { name: 'robots', content: 'index, follow' },
        { name: 'author', content: 'Suriyaprakash Somu' },
        { name: 'geo.region', content: 'IN-TN' },
        {
          name: 'geo.placename',
          content: 'Erode, Tamil Nadu, India',
        },
        { name: 'ai-summary', content: aiSummary },
        {
          name: 'keywords',
          content: [
            loaderData.category.name.toLowerCase(),
            ...loaderData.posts.flatMap((p) => p.frontmatter.tags),
          ].join(', '),
        },
        {
          property: 'og:title',
          content: title,
        },
        {
          property: 'og:description',
          content: description,
        },
        { property: 'og:url', content: categoryUrl },
        { property: 'og:type', content: 'website' },
        {
          property: 'og:image',
          content: 'https://www.suriyaprakash.in/og/default.png',
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:image',
          content: 'https://www.suriyaprakash.in/og/default.png',
        },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
      ],
      links: [
        {
          rel: 'canonical',
          href: categoryUrl,
        },
        {
          rel: 'alternate',
          type: 'application/rss+xml',
          title: 'RSS',
          href: 'https://www.suriyaprakash.in/rss.xml',
        },
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: title,
            description,
            url: categoryUrl,
            isPartOf: {
              '@type': 'Blog',
              name: 'Suriyaprakash Somu Blog',
              url: 'https://www.suriyaprakash.in/blog',
            },
          }),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: `${loaderData.category.name} Blog Articles`,
            itemListElement: loaderData.posts.map((post, index) => ({
              '@type': 'ListItem',
              position: (loaderData.page - 1) * PAGE_SIZE + index + 1,
              url: `https://www.suriyaprakash.in/blog/${post.category}/${post.slug}`,
              name: post.frontmatter.title,
            })),
          }),
        },
      ],
    }
  },
})

function CategoryPage() {
  const {
    category: cat,
    posts,
    page,
    totalPages,
    totalItems,
  } = Route.useLoaderData()
  const series = [
    ...new Set(posts.map((post) => post.frontmatter.series).filter(Boolean)),
  ]
  const standalone = posts.filter((post) => !post.frontmatter.series)

  return (
    <div className="min-h-screen bg-ink text-cream-50">
      <EditorialHero
        back={{ to: '/blog', label: 'Back to Blog' }}
        eyebrow="Category / Field notes"
        title={cat.name}
        description={cat.description || undefined}
        meta={
          <p className="preview-mono text-[10px] font-bold uppercase tracking-[0.2em] text-sage-200">
            {totalItems} {totalItems === 1 ? 'article' : 'articles'}
          </p>
        }
      />

      <section className="bg-ink py-20 sm:py-28">
        <div className="container-custom">
          <SectionHeader
            eyebrow="Category index"
            title="Learning paths in"
            accent={cat.name}
            tone="dark"
          />
          <div className="mt-12 sm:mt-16">
            {posts.length > 0 ? (
              <div className="space-y-16">
                {series.map((name) => (
                  <section key={name}>
                    <h2 className="mb-6 font-heading text-3xl text-sage-200 sm:text-4xl">
                      {name}
                    </h2>
                    <p className="mb-6 text-sm leading-7 text-cream-50/80">
                      Read in order, starting with the fundamentals and building
                      toward implementation and debugging.
                    </p>
                    <EditorialPostList
                      posts={posts
                        .filter((post) => post.frontmatter.series === name)
                        .sort(
                          (a, b) =>
                            (a.frontmatter.seriesOrder ?? 99) -
                            (b.frontmatter.seriesOrder ?? 99),
                        )}
                      categories={[cat]}
                    />
                  </section>
                ))}
                {standalone.length > 0 && (
                  <EditorialPostList posts={standalone} categories={[cat]} />
                )}
              </div>
            ) : (
              <Reveal>
                <div className="border-y border-white/15 py-16">
                  <p className="font-heading text-3xl sm:text-5xl">
                    No articles in this category yet.
                  </p>
                </div>
              </Reveal>
            )}
          </div>
          <Pagination
            page={page}
            totalPages={totalPages}
            to="/blog/$category"
            params={{ category: cat.slug }}
          />
        </div>
      </section>
    </div>
  )
}
